import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { Building2, Users, ClipboardCheck, TrendingUp, Brain, Sparkles } from 'lucide-react';
import { api } from '../../api.js';
import { Card, CardHeader, ScoreBadge, Badge, PageHeader, Spinner, EmptyState } from '../../components/ui.jsx';

export default function CompanyInsightsPage() {
  const { data, isLoading } = useQuery({ queryKey: ['company-insights'], queryFn: api.companyInsights });

  if (isLoading || !data) return <div className="min-h-[40vh] grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;
  if (!data.totals?.users_assessed) {
    return (
      <>
        <PageHeader title="Company insights" description="Workforce capability across your organisation." />
        <EmptyState icon={Building2} title="No assessment data yet"
          description="Once your team starts completing assessments, you'll see capability roll-ups, department comparisons, AI readiness, and trend tracking here." />
      </>
    );
  }

  const t = data.totals;
  const completion = t.total_users > 0 ? Math.round((t.users_assessed / t.total_users) * 100) : 0;

  const departmentData = (data.departments || []).filter(d => d.user_count > 0).map(d => ({
    name: prettyDept(d.department_type), users: d.user_count, score: d.avg_overall || 0
  }));

  const moduleData = (data.modules || []).map(m => ({
    name: m.name, slug: m.slug, sessions: m.sessions, score: m.avg_score || 0
  }));

  return (
    <>
      <PageHeader title="Company insights"
        description={`${data.company.name} — workforce capability across the organisation.`} />

      {/* AI insight panel */}
      {data.latest_insight ? (
        <Card className="mb-6 bg-gradient-to-br from-primary-50/40 via-white to-accent-50/40">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-700 to-accent-600 grid place-items-center text-white shrink-0"><Sparkles className="w-4 h-4" /></div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider font-semibold text-accent-700 mb-1">AI insight · {new Date(data.latest_insight.generated_at).toLocaleDateString('en-AU')}</div>
              <h3 className="text-lg font-semibold text-ink-900 mb-2">{data.latest_insight.content.headline}</h3>
              {data.latest_insight.content.notable_patterns ? <p className="text-sm text-ink-700 leading-relaxed">{data.latest_insight.content.notable_patterns}</p> : null}
            </div>
          </div>
        </Card>
      ) : null}

      {/* Key metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric label="Active users" value={t.total_users} icon={Users} />
        <Metric label="Users assessed" value={t.users_assessed} subtitle={`${completion}% of headcount`} icon={ClipboardCheck} />
        <Metric label="Sessions completed" value={t.sessions_completed} icon={TrendingUp} />
        <Metric label="Departments" value={t.total_groups} icon={Building2} />
      </div>

      {/* AI Readiness + Change Readiness side by side */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-gradient-to-br from-primary-50/30 to-accent-50/20">
          <CardHeader title="AI readiness scorecard" subtitle="Capability to use AI tools effectively" />
          {data.ai_readiness ? (
            <div className="flex items-center gap-6">
              <ScoreBadge score={data.ai_readiness.avg} large />
              <div className="text-sm text-ink-600">
                <div><strong className="text-ink-900">{data.ai_readiness.assessed}</strong> people assessed</div>
                <div>Range: {data.ai_readiness.min}–{data.ai_readiness.max}</div>
              </div>
              <Brain className="w-12 h-12 text-accent-300 ml-auto" />
            </div>
          ) : <p className="text-sm text-ink-500">No AI Readiness assessments completed yet.</p>}
        </Card>

        <Card>
          <CardHeader title="Change readiness" subtitle="Resilience under organisational change" />
          {data.change_readiness ? (
            <div className="flex items-center gap-6">
              <ScoreBadge score={data.change_readiness.score} large />
              <div className="text-sm text-ink-600">
                <div><strong className="text-ink-900">{data.change_readiness.assessed}</strong> people assessed</div>
                <div className="text-xs mt-1">From Change Resilience module</div>
              </div>
            </div>
          ) : <p className="text-sm text-ink-500">No Change Resilience assessments completed yet.</p>}
        </Card>
      </div>

      {/* Department comparison */}
      {departmentData.length > 0 ? (
        <Card className="mb-6">
          <CardHeader title="Department comparison" subtitle="Average capability score by department" />
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={departmentData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748B' }} />
                <Tooltip cursor={{ fill: '#F1F5F9' }} />
                <Bar dataKey="score" fill="url(#deptGrad)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="deptGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" />
                    <stop offset="100%" stopColor="#1E40AF" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      ) : null}

      {/* Module roll-up */}
      {moduleData.length > 0 ? (
        <Card>
          <CardHeader title="Module participation" subtitle="Where the data is coming from" />
          <div className="space-y-2">
            {moduleData.map(m => (
              <div key={m.slug} className="flex items-center gap-3">
                <ScoreBadge score={m.score || 0} />
                <div className="flex-1">
                  <div className="font-medium text-ink-900 text-sm">{m.name}</div>
                  <div className="text-xs text-ink-500">{m.sessions} session{m.sessions === 1 ? '' : 's'}</div>
                </div>
                <div className="w-32 h-2 bg-ink-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary-600 to-accent-600" style={{ width: `${(m.score || 0)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </>
  );
}

function Metric({ label, value, subtitle, icon: Icon }) {
  return (
    <Card padding={false} className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="text-xs uppercase tracking-wider font-semibold text-ink-500">{label}</div>
        <Icon className="w-4 h-4 text-ink-400" />
      </div>
      <div className="text-2xl font-semibold text-ink-900">{value}</div>
      {subtitle ? <div className="text-xs text-ink-500 mt-0.5">{subtitle}</div> : null}
    </Card>
  );
}

function prettyDept(t) {
  if (!t) return 'Other';
  return t.split('_').map(s => s[0].toUpperCase() + s.slice(1)).join(' ');
}
